import { Product } from '@shared/components/ui/Product';
import { Button } from '@shared/components/ui/button';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfitabilitySummaryCogs } from '../../hooks';
import type { ProfitabilitySummaryBreakdownQuery, ProfitabilitySummaryCogsTotals } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';

const PER_PAGE = 20;

interface ProfitabilitySummaryCogsTableProps {
  filters: ProfitabilitySummaryBreakdownQuery | undefined;
  locale: string;
  embedded?: boolean;
  enabled?: boolean;
}

function formatQuantity(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

function CogsTotalsRow({
  label,
  totals,
  locale,
  currency,
}: {
  label: string;
  totals: ProfitabilitySummaryCogsTotals;
  locale: string;
  currency: string;
}) {
  return (
    <TableRow className="border-border bg-muted/40 font-medium hover:bg-muted/40">
      <TableCell className="text-sm text-foreground">{label}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">{totals.orders_count.toLocaleString(locale)}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">{formatQuantity(totals.quantity, locale)}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityAmount(totals.average_unit_cost, locale, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityAmount(totals.total_cogs, locale, currency)}
      </TableCell>
    </TableRow>
  );
}

export const ProfitabilitySummaryCogsTable = ({
  filters,
  locale,
  embedded = false,
  enabled = true,
}: ProfitabilitySummaryCogsTableProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    filters?.start_date,
    filters?.end_date,
    filters?.shop_id,
    filters?.payment_method_id,
    filters?.shipment_status_id,
  ]);

  const query = useMemo(() => (filters ? { ...filters, page, per_page: PER_PAGE } : undefined), [filters, page]);

  const { data, isLoading, isError, isFetching } = useProfitabilitySummaryCogs(query, !!filters && enabled);
  const rows = data?.data ?? [];
  const meta = data?.meta;
  const colCount = 5;
  const showPagination = meta != null && (meta.total > 0 || meta.last_page > 1);

  return (
    <div data-testid="profitability-summary-cogs-table">
      {!embedded && (
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-lg font-semibold text-title">{t('summary.cogs.title')}</h2>
          <InfoTooltip
            text={t('summary.cogs.hint')}
            dataTestId="profitability-summary-cogs-info"
            iconClassName="w-4 h-4"
          />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">{t('summary.cogs.col_product')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.cogs.col_orders')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.cogs.col_quantity')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.cogs.col_avg_unit_cost')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.cogs.col_total_cogs')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">
                  …
                </TableCell>
              </TableRow>
            )}

            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('empty.error.title')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('summary.cogs.empty')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map((row) => (
                <TableRow
                  key={row.product_id}
                  className="border-border"
                  data-testid={`profitability-summary-cogs-row-${row.product_id}`}
                >
                  <TableCell className="max-w-[320px]">
                    <Product
                      id={row.product_id}
                      name={row.name}
                      code={row.code ?? undefined}
                      src={row.image_url?.trim() ? row.image_url : ''}
                      className="min-w-0 max-w-full justify-start"
                      showTooltip
                      showImageModal={Boolean(row.image_url?.trim())}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {row.orders_count.toLocaleString(locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatQuantity(row.quantity, locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatProfitabilityAmount(row.average_unit_cost, locale, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-medium">
                    {formatProfitabilityAmount(row.total_cogs, locale, currency)}
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && !isError && data?.totals && rows.length > 0 && (
              <CogsTotalsRow
                label={t('summary.cogs.total_row')}
                totals={data.totals}
                locale={locale}
                currency={currency}
              />
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && meta && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t('pagination.total', { total: meta.total })}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('pagination.page', { current: meta.current_page, last: meta.last_page })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={meta.current_page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="profitability-summary-cogs-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={meta.current_page >= meta.last_page || isFetching}
              onClick={() => setPage((p) => p + 1)}
              data-testid="profitability-summary-cogs-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
